import CalendarMonth from "@mui/icons-material/CalendarMonth";
import ContentCopy from "@mui/icons-material/ContentCopy";
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useCallback, useState } from "react";

type CalendarSubscriptionUrlSectionProps = {
  /** Null until the user generates a subscription. */
  url: string | null;
  saving: boolean;
  disabled: boolean;
  /** False while the chosen scope still needs a filter before it can be saved. */
  canGenerate: boolean;
  onGenerate: () => void;
  onRevoke: () => void;
};

/** The subscription URL with copy and revoke, or the button to create it. */
export function CalendarSubscriptionUrlSection({
  url,
  saving,
  disabled,
  canGenerate,
  onGenerate,
  onRevoke,
}: CalendarSubscriptionUrlSectionProps) {
  const alerts = useAlerts();
  const [copied, setCopied] = useState(false);
  const copyToClipboard = useCopyToClipboard();

  const handleCopy = useCallback(async () => {
    if (url) {
      try {
        await copyToClipboard(url);
        setCopied(true);
        alerts.success("URL copied to clipboard");
        setTimeout(() => setCopied(false), 2000);
      } catch {
        alerts.error("Failed to copy URL");
      }
    }
  }, [url, alerts, copyToClipboard]);

  if (!url) {
    return (
      <>
        <Alert severity="warning" variant="outlined">
          <Typography variant="body2">
            You haven't set up a calendar subscription yet. Generate a URL to get started.
          </Typography>
        </Alert>

        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CalendarMonth />}
          onClick={onGenerate}
          disabled={disabled || !canGenerate}
        >
          Generate Subscription URL
        </Button>
      </>
    );
  }

  return (
    <>
      <TextField
        label="Subscription URL"
        value={url}
        fullWidth
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={copied ? "Copied!" : "Copy URL"}>
                <IconButton onClick={handleCopy} edge="end">
                  <ContentCopy color={copied ? "success" : "inherit"} />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiInputBase-input": {
            fontFamily: "monospace",
            fontSize: "0.85rem",
          },
        }}
      />

      <Alert severity="info" variant="outlined">
        <Typography variant="body2">
          <strong>How to use:</strong> Copy this URL and add it as a calendar subscription in your
          calendar app. Changes may take up to 24 hours to appear (depending on your calendar app).
        </Typography>
      </Alert>

      <Typography variant="caption" color="text.secondary">
        <strong>Privacy note:</strong> This URL contains a private token, so anyone with it can see
        your tasks. If it's been shared by mistake, revoke calendar access to turn it off, then
        generate a new URL.{" "}
        <Link
          component="button"
          variant="caption"
          color="error"
          onClick={onRevoke}
          disabled={disabled}
          sx={{ verticalAlign: "baseline", cursor: disabled ? "default" : "pointer" }}
        >
          Revoke calendar access
        </Link>
      </Typography>
    </>
  );
}
